// The picture of a subject: an organisation's logo, a person's avatar.
//
// These render BEFORE any media exists, and they keep rendering when a fetch was refused. The
// mark falls back to INITIALS whenever there is no picture on file — a person behind a LinkedIn
// auth wall whose media row is `blocked`, an organisation nobody has fetched yet. It never
// falls back to a stock silhouette: a picture we do not have is stated, not invented.
//
// A subject with no media is NOT discovered by requesting its image and catching the 404: that
// costs one failed request and one console error per faceless row, and BUILD-BRIEF §7.5 asks
// for a clean console. `mediaPresence.ts` asks once per page which subjects have a picture at
// all, and only those get an <img>. `onError` stays as the last line of defence (a file deleted
// under us, a stale cache) so a broken image can never render.
//
// The media route is owned by the media agent (BUILD-BRIEF §4). These two components are the
// scaffold's, so every page can show a subject before that route answers.
import { useEffect, useState } from 'react';

import { initials } from '../../shared/format.js';
import { useHasMedia } from './mediaPresence.js';

/**
 * How large the mark stands. There are two, and the pixels are in `main.css` (`.idmark--row`,
 * `.idmark--lg`) so a mark can never be sized past the initials that have to be readable in it:
 * `row` is a board row, a table cell, a name in running text; `lg` is a masthead, where the
 * subject is named once and its picture is worth reading.
 */
export type MarkScale = 'row' | 'lg';

interface MarkProps {
  id: number;
  name: string | null | undefined;
  scale?: MarkScale;
  className?: string;
  title?: string;
}

function IdMark({ id, name, scale = 'row', kind, subjectKind, className, title }: MarkProps & {
  kind: 'logo' | 'avatar';
  subjectKind: 'organisation' | 'person';
}) {
  const src = `/api/media/${subjectKind}/${id}/${kind}`;
  const hasMedia = useHasMedia(subjectKind, kind, id);
  const [failed, setFailed] = useState(false);
  // A new subject gets a fresh attempt: without this, scrolling a list would keep the previous
  // row's failure and show initials for a subject that does have a picture.
  useEffect(() => { setFailed(false); }, [src]);

  const label = (name ?? '').trim();
  const shape = subjectKind === 'organisation' ? 'idmark--org' : 'idmark--person';

  return (
    <span className={`idmark ${shape} idmark--${scale} ${className ?? ''}`.trim()} title={title ?? label} aria-hidden={!label}>
      {hasMedia === true && !failed
        ? <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
        : initials(label)}
    </span>
  );
}

export function OrgLogo(props: MarkProps) {
  return <IdMark {...props} kind="logo" subjectKind="organisation" />;
}

export function PersonAvatar(props: MarkProps) {
  return <IdMark {...props} kind="avatar" subjectKind="person" />;
}
